/**
 * The Inbox tab of the Shows section (SCRUM-587 S4): the things that need the person, newest first. The
 * derivation is `shows/inbox.ts` (pure, tested); this file fetches what it needs and draws the list. Each item
 * is ONE button that opens the right place — the show page for an approval, a new offer or a rider request, the
 * offer itself for a supplier's own offer.
 */
import { listIncomingOffers, listMyOffers } from "../api/offers-api";
import { getStoredUser } from "../api/http";
import { getShow, listShows, type ShowDetail } from "../api/shows-api";
import { navigate } from "../app/router";
import { formatDateTime } from "../format-datetime";
import { getLocale, t } from "../i18n";
import { describeWhen } from "../offer/offer-placement";
import { deriveInbox, type InboxItem, type InboxTarget } from "../shows/inbox";
import { el, errorText } from "../shows/dom";
import { iconEl, type IconName } from "./icons";

const ICON: Record<InboxItem["kind"], IconName> = {
  approval: "check",
  new_offer: "offers",
  reconfirm: "edit",
  declined: "close",
  rider_request: "rider",
};

export function openInboxTarget(target: InboxTarget): void {
  if (target.kind === "show") navigate({ name: "show-detail", showId: target.showId });
  else navigate({ name: "public-viewer", riderId: target.riderId, version: target.version, offerId: target.offerId });
}

/**
 * The SHOW's date as the calendar and the show page say it (`describeWhen`); a sketch, an open date or a show we
 * cannot see says «date not set yet» in words (SCRUM-630). Never the arrival time — that is the meta line.
 */
export function inboxWhenText(item: Pick<InboxItem, "when" | "isSketch">): string {
  const said = item.isSketch || !item.when ? undefined : describeWhen(item.when, getLocale());
  return said ?? t("shows.inbox.dateOpen");
}

/** The sentence of one item, in the person's language. */
export function inboxMessage(item: InboxItem): string {
  const who = item.who ?? t("shows.inbox.unknownWho");
  const when = inboxWhenText(item);
  switch (item.kind) {
    case "approval":
      return t(item.isNew ? "shows.inbox.approvalNew" : "shows.inbox.approvalAgain", { who, subject: item.subject, when });
    case "new_offer":
      return t("shows.inbox.newOffer", { who, subject: item.subject, when });
    case "reconfirm":
      return t("shows.inbox.reconfirm", { from: String(item.from), to: String(item.to), subject: item.subject, when });
    case "declined":
      return t("shows.inbox.declined", { subject: item.subject, when });
    case "rider_request":
      return t("shows.inbox.riderRequest", { subject: item.subject, when });
  }
}

/** Everything the inbox lists, read the same way for the tab and for the «Inbox» badge in the menu. */
export async function loadInboxItems(): Promise<InboxItem[]> {
  const [shows, incoming, mine] = await Promise.all([listShows(), listIncomingOffers(), listMyOffers()]);
  // The organiser's and the band's shows carry the offers and the rider requests; a supplier's own offers
  // are already in `mine`.
  const owned = shows.filter((s) => s.role !== "supplier");
  const settled = await Promise.allSettled(owned.map((s) => getShow(s.id)));
  const details = settled.flatMap((r) => (r.status === "fulfilled" ? [r.value as ShowDetail] : []));
  return deriveInbox({ myEmail: getStoredUser()?.email ?? null, shows, details, incoming, mine });
}

export function mountShowsInbox(host: HTMLElement): { dispose(): void } {
  let alive = true;
  host.replaceChildren(el("p", "offers-empty", t("common.loading")));

  void (async () => {
    try {
      const items = await loadInboxItems();
      if (!alive) return;
      render(items);
    } catch (err) {
      if (alive) host.replaceChildren(el("p", "auth-message auth-message-error", t("shows.loadFailed", { error: errorText(err) })));
    }
  })();

  function render(items: InboxItem[]): void {
    if (items.length === 0) {
      const empty = el("p", "offers-empty", t("shows.inbox.empty"));
      empty.id = "shows-inbox-empty";
      host.replaceChildren(empty);
      return;
    }
    const list = el("ul", "shows-inbox-list");
    list.id = "shows-inbox-list";
    list.setAttribute("aria-label", t("shows.inbox.listLabel"));
    for (const item of items) {
      const li = el("li");
      const open = el("button", `shows-inbox-item shows-inbox-item--${item.kind}`);
      open.type = "button";
      open.dataset.kind = item.kind;
      if (item.offerId) open.dataset.offerId = item.offerId;
      const text = el("span", "shows-inbox-text");
      text.append(el("span", "shows-inbox-message", inboxMessage(item)));
      if (item.reason) text.append(el("span", "shows-inbox-reason", t("shows.inbox.declinedReason", { reason: item.reason })));
      text.append(el("span", "shows-inbox-when", t("shows.inbox.received", { at: formatDateTime(item.at) })));
      if (item.unread) {
        open.dataset.unread = "true";
        open.classList.add("shows-inbox-item--unread");
      }
      const mark = item.unread ? el("span", "shows-inbox-new", t("shows.inbox.new")) : null;
      open.append(iconEl(ICON[item.kind], 18), text, ...(mark ? [mark] : []), el("span", "shows-inbox-open", t("shows.inbox.open")));
      open.addEventListener("click", () => openInboxTarget(item.target));
      li.append(open);
      list.append(li);
    }
    host.replaceChildren(list);
  }

  return { dispose: () => void (alive = false) };
}
